import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Box, Typography, Tooltip, Avatar, AvatarGroup, Chip, IconButton, LinearProgress // eslint-disable-line no-unused-vars
} from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import validationService from '../../services/validationService';
import ValidationHistoryPanel from '../validation/ValidationHistoryPanel';
import VersionDiffPanel from './VersionDiffPanel';

/**
 * TaskApprovalBar — Shows task-level approval status inside the split view header.
 * Displays: who approved (avatars), status label, item progress, and history access.
 *
 * Props:
 *   projectId     — current project ID
 *   taskId        — current task ID (e.g., 'features', 'user-stories')
 *   validatedItems — object of { itemId: boolean } from useValidation
 *   totalItems    — total number of items in the task
 *   currentUserMemberType — 'internal' or 'external'
 */
const TaskApprovalBar = ({ projectId, taskId, conversationId, validatedItems, totalItems, currentUserMemberType }) => {
  const [validationStatus, setValidationStatus] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [showVersionDiff, setShowVersionDiff] = useState(false);
  const [historyFetchKey, setHistoryFetchKey] = useState(0);

  const fetchStatus = useCallback(async () => {
    if (!projectId || !taskId) return;
    try {
      const summary = await validationService.getValidationSummary(projectId);
      setValidationStatus(summary?.[taskId] || null);
    } catch {
      // Silent — supplementary info
    }
  }, [projectId, taskId]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  // Count validated items
  const validatedCount = validatedItems
    ? Object.values(validatedItems).filter(Boolean).length
    : 0;
  // const itemProgress = totalItems > 0 ? (validatedCount / totalItems) * 100 : 0;

  const intVal = validationStatus?.internal_validation;
  const extVal = validationStatus?.external_validation;
  const hasValidators = validationStatus?.validators &&
    (validationStatus.validators.internal?.length > 0 || validationStatus.validators.external?.length > 0);

  // Don't render if no conversation and no validation activity
  if (!conversationId && !hasValidators && validatedCount === 0) return null;

  const getStatusChip = () => {
    const anyStale = intVal?.is_stale || extVal?.is_stale;
    if (anyStale) {
      return { label: 'Needs re-review', color: '#f9a825', bgColor: '#fff8e1', icon: <RefreshIcon sx={{ fontSize: 14 }} /> };
    }
    const hasChanges = intVal?.status === 'changes_requested' || extVal?.status === 'changes_requested';
    if (hasChanges) {
      return { label: 'Changes requested', color: '#d32f2f', bgColor: '#ffebee', icon: <ErrorOutlineIcon sx={{ fontSize: 14 }} /> };
    }
    const intApproved = intVal?.status === 'approved';
    const extApproved = extVal?.status === 'approved';
    if (intApproved && extApproved) {
      return { label: 'Fully approved', color: '#2e7d32', bgColor: '#e8f5e9', icon: <CheckCircleIcon sx={{ fontSize: 14 }} /> };
    }
    if (intApproved) {
      return { label: 'Internal approved', color: '#1976d2', bgColor: '#e3f2fd', icon: <CheckCircleIcon sx={{ fontSize: 14 }} /> };
    }
    if (extApproved) {
      return { label: 'External approved', color: '#2e7d32', bgColor: '#e8f5e9', icon: <CheckCircleIcon sx={{ fontSize: 14 }} /> };
    }
    return null;
  };

  const statusChip = getStatusChip();

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true
    });
  };

  return (
    <Box sx={{ 
      px:3
     }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 3,
          py: 1,
          // borderBottom: '1px solid #f0f0f0',
          bgcolor: '#f5f8fb',
          flexWrap: 'wrap',
          minHeight: 36,
          borderRadius: "4px"
        }}
      >
        {/* Item-level progress — hidden per request, kept for future re-enable */}
        {/*
        {totalItems > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 140 }}>
            <Typography sx={{ fontSize: 11, color: '#72849a', whiteSpace: 'nowrap' }}>
              Items reviewed
            </Typography>
            <Box sx={{ flex: 1, minWidth: 60 }}>
              <LinearProgress
                variant="determinate"
                value={itemProgress}
                sx={{
                  height: 4,
                  borderRadius: 2,
                  bgcolor: '#e8eaed',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 2,
                    bgcolor: itemProgress === 100 ? '#2e7d32' : '#1976d2',
                  }
                }}
              />
            </Box>
            <Typography sx={{ fontSize: 11, fontWeight: 500, color: '#72849a' }}>
              {validatedCount}/{totalItems}
            </Typography>
          </Box>
        )}
        */}

        {/* Task-level approval status */}
        {statusChip && (
          <Chip
            icon={statusChip.icon}
            label={statusChip.label}
            size="small"
            sx={{
              height: 22,
              fontSize: 11,
              fontWeight: 500,
              bgcolor: statusChip.bgColor,
              color: statusChip.color,
              border: 'none',
              '& .MuiChip-icon': { color: statusChip.color },
            }}
          />
        )}

        {/* Right-aligned group: avatars + action buttons */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 'auto' }}>
          {/* Approver avatars with details */}
          {(intVal?.validated_by || extVal?.validated_by) && (
            <AvatarGroup
              max={3}
              sx={{
                mr: 0.5,
                '& .MuiAvatar-root': {
                  width: 22, height: 22, fontSize: 10, fontWeight: 600,
                  border: '1.5px solid #fff',
                }
              }}
            >
              {intVal?.validated_by && (
                <Tooltip title={
                  `Internal: ${intVal.validated_by.name} — ${intVal.is_stale ? 'Stale' : intVal.status === 'approved' ? 'Approved' : 'Changes requested'}` +
                  (intVal.comment ? `\n"${intVal.comment}"` : '') +
                  (intVal.validated_at ? `\n${formatDate(intVal.validated_at)}` : '')
                }>
                  <Avatar sx={{
                    bgcolor: intVal.is_stale ? '#f9a825' :
                             intVal.status === 'changes_requested' ? '#d32f2f' : '#1976d2',
                  }}>
                    {intVal.validated_by.name?.charAt(0)?.toUpperCase() || '?'}
                  </Avatar>
                </Tooltip>
              )}
              {extVal?.validated_by && (
                <Tooltip title={
                  `External: ${extVal.validated_by.name} — ${extVal.is_stale ? 'Stale' : extVal.status === 'approved' ? 'Approved' : 'Changes requested'}` +
                  (extVal.comment ? `\n"${extVal.comment}"` : '') +
                  (extVal.validated_at ? `\n${formatDate(extVal.validated_at)}` : '')
                }>
                  <Avatar sx={{
                    bgcolor: extVal.is_stale ? '#f9a825' :
                             extVal.status === 'changes_requested' ? '#d32f2f' : '#2e7d32',
                  }}>
                    {extVal.validated_by.name?.charAt(0)?.toUpperCase() || '?'}
                  </Avatar>
                </Tooltip>
              )}
            </AvatarGroup>
          )}

          {/* Assigned but not yet reviewed indicator */}
          {hasValidators && !intVal?.validated_by && !extVal?.validated_by && (
            <Typography sx={{ fontSize: 11, color: '#a0aec0', fontStyle: 'italic', mr: 0.5 }}>
              Awaiting review
            </Typography>
          )}

          {/* Version diff button */}
          {conversationId && (
            <Tooltip title="View version changes">
              <IconButton
                size="small"
                sx={{
                  p: 0.5,
                  color: '#72849a',
                  '&:hover': { color: '#1976d2', bgcolor: 'rgba(25,118,210,0.06)' },
                }}
                onClick={() => setShowVersionDiff(true)}
              >
                <CompareArrowsIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}

          {/* Activity history button — shows validations, content edits, etc. */}
          <Tooltip title="View activity history">
            <IconButton
              size="small"
              sx={{
                p: 0.5,
                color: '#72849a',
                '&:hover': { color: '#475969', bgcolor: 'rgba(0,0,0,0.04)' },
              }}
              onClick={() => {
                setHistoryFetchKey(prev => prev + 1);
                setShowHistory(true);
              }}
            >
              <HistoryIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Validation History Panel */}
      <ValidationHistoryPanel
        open={showHistory}
        onClose={() => setShowHistory(false)}
        projectId={projectId}
        taskId={taskId}
        restrictTo="both"
        fetchKey={historyFetchKey}
        currentUserMemberType={currentUserMemberType || 'internal'}
      />

      {/* Version Diff Panel */}
      <VersionDiffPanel
        open={showVersionDiff}
        onClose={() => setShowVersionDiff(false)}
        conversationId={conversationId}
      />
    </Box>
  );
};

TaskApprovalBar.propTypes = {
  projectId: PropTypes.string.isRequired,
  taskId: PropTypes.string.isRequired,
  conversationId: PropTypes.string,
  validatedItems: PropTypes.object,
  totalItems: PropTypes.number,
  currentUserMemberType: PropTypes.string,
};

export default React.memo(TaskApprovalBar);
